/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AI TYPES - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file ai.types.ts
 * @version 1.0.0
 * @description Tipos TypeScript para el sistema de gestión de IA (Fase 5)
 * @author WASP Development Team
 * 
 * IMPORTANTE:
 * - Basado en endpoints reales: /api/ai-endpoints
 * - Compatible con backend v1.0.1-pre.0.20
 * - No modificar sin revisar backend
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════
// CAPABILITIES - Capacidades de los endpoints de IA
// ═══════════════════════════════════════════════════════════════════════════

export type AICapability = 
  | 'text'              // Generación de texto / Chat
  | 'image-gen'         // Generación de imágenes
  | 'image-analysis'    // Análisis de imágenes
  | 'audio-stt'         // Speech-to-Text
  | 'audio-tts'         // Text-to-Speech
  | 'code'              // Generación de código
  | 'embedding'         // Embeddings vectoriales
  | 'moderation'        // Moderación de contenido
  | 'translation';      // Traducción

// ═══════════════════════════════════════════════════════════════════════════
// AI ENDPOINT - Estructura principal del backend
// ═══════════════════════════════════════════════════════════════════════════

export interface AIEndpoint {
  id: string;                       // UUID único
  name: string;                     // Nombre descriptivo (ej: "IRC GPT-4")
  type: string;                     // Tipo de endpoint (ej: "openai", "anthropic", "custom")
  capabilities: AICapability[];     // Array de capacidades
  primary: string;                  // Capacidad primaria
  url: string;                      // URL del endpoint
  enabled: boolean;                 // Estado activo/inactivo
  priority: number;                 // Prioridad (menor = más prioritario)
  
  // Autenticación (opcional)
  auth?: {
    type: 'bearer' | 'api-key' | 'basic' | 'none';
    key?: string;
    token?: string;
    username?: string;
    password?: string;
  };
  
  // Metadata extendida (para Fase 5)
  metadata?: {
    description?: string;
    model?: string;                 // Modelo específico (ej: "gpt-4", "claude-3")
    maxTokens?: number;
    temperature?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
    
    // Prompts personalizados
    prompts?: AIPrompt[];
    
    // Fine-tuning
    fineTuning?: AIConfig;
    
    // Historial
    usageHistory?: AIInteraction[];
    
    // A/B Testing
    abTests?: ABTest[];
  };
  
  // Datos adicionales
  extraData?: Record<string, any>;
  
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// AI PROMPT - Prompts personalizados (subcategoría de metadata)
// ═══════════════════════════════════════════════════════════════════════════

export interface AIPrompt {
  id: string;
  name: string;
  content: string;
  variables: string[];              // Variables tipo {nombre}, {curso}
  category: string;                 // Categoría (ej: "marketing", "soporte")
  usage_count: number;
  success_rate: number;             // 0-100
  created_at: string;
  updated_at: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// AI CONFIG - Configuración fine-tuning
// ═══════════════════════════════════════════════════════════════════════════

export interface AIConfig {
  temperature: number;              // 0-2
  top_p: number;                    // 0-1
  frequency_penalty: number;        // 0-2
  presence_penalty: number;         // 0-2
  max_tokens: number;               // Límite de tokens
  model: string;                    // Modelo específico
}

// ═══════════════════════════════════════════════════════════════════════════
// AI INTERACTION - Historial de interacciones
// ═══════════════════════════════════════════════════════════════════════════

export interface AIInteraction {
  id: string;
  endpoint_id: string;              // ID del endpoint usado
  prompt_id?: string;               // ID del prompt usado (opcional)
  user_message: string;
  ai_response: string;
  tokens_used: number;
  response_time: number;            // Milisegundos
  rating?: number;                  // 1-5 estrellas
  status: 'success' | 'error' | 'timeout';
  error_message?: string;
  created_at: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// AI METRICS - Métricas agregadas
// ═══════════════════════════════════════════════════════════════════════════

export interface AIMetrics {
  total_interactions: number;
  success_rate: number;             // 0-100
  avg_response_time: number;        // Milisegundos promedio
  total_tokens: number;
  avg_rating: number;               // 1-5 promedio
  
  // Datos por día (últimos 30 días)
  interactions_by_day: {
    date: string;                   // YYYY-MM-DD
    count: number;
    success: number;
    errors: number;
  }[];
  
  // Endpoints más usados
  top_endpoints: {
    endpoint_id: string;
    name: string;
    count: number;
    success_rate: number;
  }[];
}

// ═══════════════════════════════════════════════════════════════════════════
// AB TEST - Testing A/B de endpoints/prompts
// ═══════════════════════════════════════════════════════════════════════════

export interface ABTest {
  id: string;
  name: string;
  description?: string;
  
  // Variantes
  variant_a: {
    endpoint_id: string;
    prompt_id?: string;
    name: string;
  };
  
  variant_b: {
    endpoint_id: string;
    prompt_id?: string;
    name: string;
  };
  
  // Estado
  status: 'active' | 'completed' | 'paused';
  
  // Resultados
  results: {
    variant_a: {
      interactions: number;
      success_rate: number;
      avg_response_time: number;
      avg_rating: number;
    };
    variant_b: {
      interactions: number;
      success_rate: number;
      avg_response_time: number;
      avg_rating: number;
    };
  };
  
  // Winner
  winner?: 'a' | 'b' | 'tie';
  winner_reason?: string;
  
  // Timestamps
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// API RESPONSES - Respuestas del backend
// ═══════════════════════════════════════════════════════════════════════════

export interface AIEndpointListResponse {
  success: boolean;
  endpoints: AIEndpoint[];
  count: number;
  timestamp: string;
}

export interface AIEndpointResponse {
  success: boolean;
  endpoint: AIEndpoint;
  timestamp: string;
}

export interface AIEndpointCreateResponse {
  success: boolean;
  message: string;
  endpoint: AIEndpoint;
  timestamp: string;
}

export interface AIEndpointUpdateResponse {
  success: boolean;
  message: string;
  endpoint: AIEndpoint;
  timestamp: string;
}

export interface AIEndpointDeleteResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface AIEndpointTestResponse {
  success: boolean;
  message: string;
  endpoint: {
    id: string;
    name: string;
    url: string;
  };
  testMessage: string;
  result?: {
    response: string;
    tokens_used: number;
    response_time: number;
  };
  note?: string;
  timestamp: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// FILTER & SEARCH - Tipos para filtros y búsqueda
// ═══════════════════════════════════════════════════════════════════════════

export interface AIEndpointFilters {
  capability?: AICapability;
  enabled?: boolean;
  type?: string;
  search?: string;                  // Búsqueda por nombre
}

export interface AIPromptFilters {
  category?: string;
  search?: string;
}

export interface AIInteractionFilters {
  endpoint_id?: string;
  prompt_id?: string;
  status?: 'success' | 'error' | 'timeout';
  rating?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// FORM DATA - Tipos para formularios
// ═══════════════════════════════════════════════════════════════════════════

export interface AIEndpointFormData {
  name: string;
  type: string;
  capabilities: AICapability[];
  primary: AICapability;
  url: string;
  enabled?: boolean;
  priority?: number;
  auth?: {
    type: 'bearer' | 'api-key' | 'basic' | 'none';
    key?: string;
    token?: string;
  };
  metadata?: {
    description?: string;
    model?: string;
    maxTokens?: number;
    temperature?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
  };
}

export interface AIPromptFormData {
  name: string;
  content: string;
  category: string;
  variables?: string[];
}

export interface AIConfigFormData {
  temperature: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
  max_tokens: number;
  model: string;
}

export interface ABTestFormData {
  name: string;
  description?: string;
  variant_a: {
    endpoint_id: string;
    prompt_id?: string;
  };
  variant_b: {
    endpoint_id: string;
    prompt_id?: string;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// VALIDATION - Tipos para validación
// ═══════════════════════════════════════════════════════════════════════════

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS - Constantes útiles
// ═══════════════════════════════════════════════════════════════════════════

export const AI_CAPABILITIES: AICapability[] = [
  'text',
  'image-gen',
  'image-analysis',
  'audio-stt',
  'audio-tts',
  'code',
  'embedding',
  'moderation',
  'translation'
];

export const AI_CAPABILITY_LABELS: Record<AICapability, string> = {
  'text': 'Generación de Texto / Chat',
  'image-gen': 'Generación de Imágenes',
  'image-analysis': 'Análisis de Imágenes',
  'audio-stt': 'Speech-to-Text',
  'audio-tts': 'Text-to-Speech',
  'code': 'Generación de Código',
  'embedding': 'Embeddings Vectoriales',
  'moderation': 'Moderación de Contenido',
  'translation': 'Traducción'
};

export const DEFAULT_AI_CONFIG: AIConfig = {
  temperature: 0.7,
  top_p: 0.9,
  frequency_penalty: 0.0,
  presence_penalty: 0.0,
  max_tokens: 1000,
  model: 'gpt-4'
};

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════